import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, X, Star, User } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { MovieCard, MovieCardSkeleton } from '../components/MovieCard';
import { filmsApi, creatorsApi } from '../lib/api';
import CreatorProfileModal from '../components/CreatorProfileModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [films, setFilms] = useState([]);
  const [creators, setCreators] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);

  // Filters
  const [selectedGenre, setSelectedGenre] = useState('');
  const [durationFilter, setDurationFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Fetch genres on mount
  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const data = await filmsApi.getGenres();
        setGenres(data);
      } catch (error) {
        console.error('Failed to fetch genres:', error);
      }
    };
    fetchGenres();
  }, []);

  // Search when params change
  useEffect(() => {
    const performSearch = async () => {
      setLoading(true);
      try {
        const params = {
          search: query || undefined,
          genre: selectedGenre || undefined,
          duration_filter: durationFilter || undefined,
          sort_by: sortBy,
          limit: 50,
        };

        const [filmsData, creatorsData] = await Promise.all([
          filmsApi.getAll(params),
          query ? creatorsApi.search(query) : Promise.resolve({ creators: [] }),
        ]);

        setFilms(filmsData);
        setCreators(creatorsData.creators || []);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [query, selectedGenre, durationFilter, sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ q: query });
  };

  const clearFilters = () => {
    setSelectedGenre('');
    setDurationFilter('');
    setSortBy('newest');
    setQuery('');
    setSearchParams({});
  };

  const hasFilters = selectedGenre || durationFilter || sortBy !== 'newest' || query;

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="search-page">
      <Navbar />

      <div className="pt-24 pb-12 w-full px-4 md:px-8 lg:px-[4%]">
        {/* Search Header */}
        <div className="mb-8">
          <h1
            className="text-3xl md:text-4xl font-bold tracking-tighter mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Search
          </h1>

          {/* Search Input */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="relative max-w-xl">
              <input
                type="text"
                placeholder="Search films, creators, genres..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full form-input pl-12 pr-4 py-3 rounded-full"
                data-testid="search-input"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
          </form>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Filter className="w-4 h-4" />
              Filters:
            </div>

            {/* Genre Filter */}
            <Select value={selectedGenre || 'all'} onValueChange={(v) => setSelectedGenre(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40 bg-[#0F0B1E] border-[#8B5CF6]/20" data-testid="genre-filter">
                <SelectValue placeholder="Genre" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0B1E] border-[#8B5CF6]/20">
                <SelectItem value="all">All Genres</SelectItem>
                {genres.map((genre) => (
                  <SelectItem key={genre} value={genre}>
                    {genre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Duration Filter */}
            <Select value={durationFilter || 'all'} onValueChange={(v) => setDurationFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40 bg-[#0F0B1E] border-[#8B5CF6]/20" data-testid="duration-filter">
                <SelectValue placeholder="Duration" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0B1E] border-[#8B5CF6]/20">
                <SelectItem value="all">Any Duration</SelectItem>
                <SelectItem value="short">Under 5 min</SelectItem>
                <SelectItem value="medium">5-15 min</SelectItem>
                <SelectItem value="long">Over 15 min</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 bg-[#0F0B1E] border-[#8B5CF6]/20" data-testid="sort-filter">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0B1E] border-[#8B5CF6]/20">
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="popular">Most Viewed</SelectItem>
                <SelectItem value="rating">Top Rated</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-sm text-[#8B5CF6] hover:text-[#A78BFA] transition-colors"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="mb-4">
          <p className="text-gray-400">
            {loading ? 'Searching...' : `${films.length} films${creators.length > 0 ? `, ${creators.length} creators` : ''} found`}
          </p>
        </div>

        {/* Creator Results */}
        {creators.length > 0 && (
          <div className="mb-8" data-testid="creator-results">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <User className="w-4 h-4 text-[#8B5CF6]" /> Creators
            </h2>
            <div className="flex flex-wrap gap-3">
              {creators.map(c => (
                <button key={c.creator}
                  onClick={() => setSelectedCreator(c.creator)}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-[#8B5CF6]/40 hover:bg-[#8B5CF6]/5 transition-all group"
                  data-testid={`creator-result-${c.creator}`}>
                  <div className="w-9 h-9 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-[#8B5CF6]" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium group-hover:text-[#8B5CF6] transition-colors">{c.creator}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="flex items-center gap-0.5 text-[#00FF9D]">
                        <Star className="w-2.5 h-2.5 fill-[#00FF9D]" /> {c.avg_rating?.toFixed(1) || '0.0'}
                      </span>
                      <span>{c.content_count} title{c.content_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Films Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex justify-center">
                <MovieCardSkeleton />
              </div>
            ))}
          </div>
        ) : films.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {films.map((film) => (
              <div key={film.film_id} className="flex justify-center">
                <MovieCard film={film} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Search className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <h2 className="text-xl font-semibold mb-2">No films found</h2>
            <p className="text-gray-400">
              Try adjusting your search or filters
            </p>
          </div>
        )}
      </div>

      {selectedCreator && (
        <CreatorProfileModal
          creatorName={selectedCreator}
          onClose={() => setSelectedCreator(null)}
        />
      )}
    </div>
  );
};

export default SearchPage;
